/**
 * SFMC blocks — the palette's real source of truth.
 *
 * Blocks live in SFMC Content Builder, not in this app. Each one carries its
 * own guardrails as an `@GUARDRAILS` JSON comment at the top of its HTML, so
 * changing a copy limit is a Content Builder edit — no code change, no release.
 *
 *   SFMC block  ──/api/sfmc-blocks──▶  this service  ──▶  palette + inspector
 *
 * Two things the backend must get right (both cost real debugging time in the
 * worker implementation, so they're called out here):
 *
 *  1. A block's markup lives in EITHER `asset.content` OR
 *     `asset.slots[key].blocks[].content`. Read only `content` and fully-built
 *     blocks come back looking empty.
 *  2. `Shared Content` sits in the parent Business Unit and needs `scope=shared`
 *     plus a package scoped to that BU — a normal token can't see it.
 */
import type { Guardrails } from "./guardrails";
import { balanceBlockMarkup, reportBalance } from "./echMarkupBalance";

export interface SfmcBlock {
  id: number | string;
  name: string;
  /** markup with `{{token}}` placeholders, guardrail comment already stripped */
  html: string;
  guardrails?: Guardrails;
  hasGuardrails?: boolean;
}

export interface SfmcBlocksResponse {
  ok?: boolean;
  configured?: boolean;
  folder?: string;
  count?: number;
  blocks?: SfmcBlock[];
}

/**
 * The RAW `@GUARDRAILS` JSON, whatever shape it declares.
 *
 * `parseGuardrails` below narrows to a governed block by requiring `module`,
 * which is right for a content block and wrong for a TEMPLATE DEFINITION block:
 * `ECH_TPL_*` carries `{"kind":"template","template":…,"blocks":[…]}` and no
 * `module` at all, so the narrowing silently returned null and the whole
 * template layer had nothing to read (audit row 23). The monolith's `__grSync`
 * stores the parsed object unfiltered and lets each consumer decide, which is
 * what this exposes. Tolerates a missing or malformed comment.
 */
/**
 * ── THE HEADER ENDS WHERE ITS JSON ENDS, NOT AT THE FIRST `-->` (2026-09-08) ──────
 *
 * Layne's Contact Us header (asset 1028595, 20.8 KB, 18 fields + dataSource) carried
 * four `-->` INSIDE note strings — "exact syntax: <!--TREATMENT:none-->" and the like.
 * The lazy `[\s\S]*?@?-->` stopped at the first of them, `JSON.parse` failed on the
 * truncated text, and the block registered with NO guardrails at all: no fields, no
 * data source, the old per-division swap running, and the one url field the panel
 * could derive from the markup showing the division's link as a grey placeholder
 * (BUG-CONTACTUS-PLACEHOLDER-20260908 — measured on dev, POR, both templates).
 * Eighteen declared fields vanished without a message anywhere.
 *
 * So the JSON is found by BRACE MATCHING from the first `{` after `@GUARDRAILS`, with
 * string and escape awareness, and the comment is considered to end at the first
 * `@-->` / `-->` AFTER that closing brace. The lazy match stays as the fallback for a
 * header that is not balanced JSON, so nothing that parsed before parses differently.
 * (A `-->` inside an HTML comment is still invalid HTML — Layne is asked to avoid it —
 * but a note she writes must not be able to silently delete her own declaration.)
 */
export function grComment(html: string): { json: string; start: number; end: number } | null {
  const h = String(html || "");
  const open = h.search(/<!--\s*@GUARDRAILS/);
  if (open < 0) return null;
  const brace = h.indexOf("{", open);
  const lazy = h.slice(open).match(/<!--\s*@GUARDRAILS([\s\S]*?)@?-->/);
  const fallback = lazy ? { json: lazy[1].trim(), start: open, end: open + lazy[0].length } : null;
  if (brace < 0) return fallback;
  let depth = 0, inStr = false, esc = false, i = brace;
  for (; i < h.length; i++) {
    const c = h[i];
    if (inStr) { if (esc) esc = false; else if (c === "\\") esc = true; else if (c === '"') inStr = false; continue; }
    if (c === '"') { inStr = true; continue; }
    if (c === "{") depth++;
    else if (c === "}") { depth--; if (depth === 0) { i++; break; } }
  }
  if (depth !== 0) return fallback;
  const close = h.slice(i).match(/^\s*@?--!?>(?=\s*|$)/);
  if (!close) return fallback;
  return { json: h.slice(brace, i), start: open, end: i + close[0].length };
}

export function parseGrJson(html: string): Record<string, unknown> | null {
  const c = grComment(html);
  if (!c) return null;
  try {
    const parsed = JSON.parse(c.json) as Record<string, unknown>;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** Pull the guardrail comment out of a block's HTML. Tolerates a missing or
 *  malformed comment — a block without one is simply not governed. */
export function parseGuardrails(html: string): Guardrails | null {
  const parsed = parseGrJson(html) as Guardrails | null;
  return parsed && parsed.module ? parsed : null;
}

/** Remove the guardrail comment so it never ships inside an email. */
export function stripGuardrails(html: string): string {
  const c = grComment(html);
  if (!c) return html;
  /* the whole comment, to the terminator AFTER the balanced JSON — a lazy strip left
     the tail of a header containing `-->` inside the email (2026-09-08) */
  return html.slice(0, c.start) + html.slice(c.end).replace(/^\s*/, "");
}

/** A block turned into something the palette can render. */
export interface SfmcModule {
  key: string;
  label: string;
  /** the block's own markup — this app never authors email HTML for it */
  html: string;
  guardrails: Guardrails;
  /** starting data, from each field's declared default */
  defaults: Record<string, string>;
  /** template grouping, when the block declares one */
  template?: string;
  order?: number;
  /**
   * findings from the tag-balance gate (echMarkupBalance) — present only when the
   * block arrived unbalanced. A trimmed tail is repaired AND reported; anything else
   * is reported and left as written.
   */
  markupWarnings?: string[];
}

/** Build the palette entry for one block. Returns null if it isn't governed. */
export function toModule(block: SfmcBlock): SfmcModule | null {
  const gr = block.guardrails ?? parseGuardrails(block.html);
  if (!gr?.module) return null;
  const fields = gr.fields ?? {};
  const defaults: Record<string, string> = {};
  for (const [k, rule] of Object.entries(fields)) defaults[k] = rule.default ?? "";
  /* THE GATE (2026-09-10): a block is placed as one row of the 600px wrapper, so one
     stray `</table>` at its tail shuts the wrapper for everything after it. Count,
     repair a surplus tail, report anything else — never trust the feed's markup bare. */
  const gate = balanceBlockMarkup(stripGuardrails(block.html), block.name || `sfmc_${block.id}`);
  reportBalance(gate);
  const m: SfmcModule = {
    key: `sfmc_${block.id}`,
    label: gr.label || block.name,
    html: gate.html,
    guardrails: gr,
    defaults,
    template: gr.template,
    order: gr.order,
  };
  if (gate.warnings.length) m.markupWarnings = gate.warnings;
  return m;
}

/**
 * Blocks that belong to a named template, in the order they appear in it.
 * Powers the Template filter — a live check that the set is complete.
 */
export function templateSet(modules: SfmcModule[], template?: string): SfmcModule[] {
  return modules
    .filter((m) => m.order && (!template || m.template === template))
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
}

/** Every distinct template represented in the block library. */
export function templateNames(modules: SfmcModule[]): string[] {
  return [...new Set(modules.map((m) => m.template).filter(Boolean) as string[])].sort();
}

/**
 * The blank-canvas document shell, when SFMC provides one.
 * Recognised by name; it's a template, not a palette block.
 */
export function findShell(blocks: SfmcBlock[]): string | null {
  const hit = blocks.find((b) => /blank[_-]?template/i.test(b.name || ""));
  if (!hit) return null;
  const gate = balanceBlockMarkup(stripGuardrails(hit.html), hit.name || "shell");
  reportBalance(gate);
  return gate.html;
}

/**
 * A shell a TEMPLATE names (`"shell":"ECH_V2_Shell"` in its @GUARDRAILS), by exact
 * name, case-insensitive (2026-10-06). The backend serves `ECH_V2_Shell_TEST` under its
 * base name in dev/QA/UAT, so the template names the base. null when absent — the
 * caller then falls back to the default `findShell`. Same balance gate as findShell.
 */
export function findShellNamed(blocks: SfmcBlock[], name: string): string | null {
  const want = String(name || "").trim().toLowerCase();
  if (!want) return null;
  const hit = (blocks || []).find((b) => String(b.name || "").toLowerCase() === want);
  if (!hit || !hit.html) return null;
  const gate = balanceBlockMarkup(stripGuardrails(hit.html), hit.name || "shell");
  reportBalance(gate);
  return gate.html;
}

/** a document shell, never a palette block: `ECH_Blank_Template`, `ECH_V2_Shell` */
export const isShellName = (name: string): boolean => /blank[_-]?template|_shell$/i.test(name || "");

const isTemplateAsset = (name: string) => /_template$|TEMPLATE$/i.test(name || "");

/** Turn a raw API response into the palette's modules plus the shell. */
export function buildRegistry(res: SfmcBlocksResponse): {
  modules: SfmcModule[];
  shell: string | null;
} {
  const blocks = res.blocks ?? [];
  const modules: SfmcModule[] = [];
  for (const b of blocks) {
    if (isTemplateAsset(b.name)) continue; // shells aren't palette blocks
    const m = toModule(b);
    if (m) modules.push(m);
  }
  return { modules, shell: findShell(blocks) };
}
